import { useForm, Controller } from "react-hook-form";
import {
  collection,
  addDoc,
  Timestamp,
} from "firebase/firestore";
import {
  FormErrorMessage,
  FormLabel,
  FormControl,
  Input,
  Button,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  Checkbox,
  Box,
  Text,
  ButtonGroup,
  Tag,
} from "@chakra-ui/react";
import { db } from "firebaseConfig";
import { useForfait } from "hooks/useForfait";
import { useClient } from "hooks/useClient";
import { useEffect, useState } from "react";
import { useSitesDuForfait } from "hooks/useSitesDuForfait";
import UserIntervention from "components/user/UserIntervention";
import InterventionSites from "components/forms/InterventionSites";
import moment from "moment";
import { champsSitesIntervention } from "utils";

interface InterventionForfaitFormProps {
  onCloseModal: () => void;
  newData: (data: ForfaitType[]) => void;
  updateData: ForfaitType[];
}

export default function InterventionForfaitForm(
  props: InterventionForfaitFormProps
) {
  const {
    handleSubmit,
    register,
    setValue,
    control,
    formState: { errors, isSubmitting },
  } = useForm<InterventionType>();

  const [selectedIdForfait, setSelectedIdForfait] = useState<{
    id: string;
    temps: number;
  }>();
  const { fetch } = useForfait();
  const { getClientField } = useClient();
  const { getForfaitField } = useForfait();
  const [clientName, setClientName] = useState("");
  const choix = useSitesDuForfait(props.updateData[0].id);
  // Les sites du forfait sont cochés par défaut, et se décochent
  useEffect(() => {
    if (choix.pret) {
      setValue("idsites", choix.parDefaut);
      setValue("cms", choix.cmsParDefaut);
    }
  }, [choix.pret, choix.parDefaut, choix.cmsParDefaut, setValue]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const client = await getClientField(
          props.updateData[0].idclient,
          "raison_sociale"
        );
        setClientName(client);
        setValue("idforfait", props.updateData[0].id);
        const tempsAsString = await getForfaitField(
          props.updateData[0].id,
          "temps"
        );
        const temps = parseFloat(tempsAsString);
        setSelectedIdForfait({ id: props.updateData[0].id, temps: temps });

        // Récupérer et pré-remplir le site_internet du forfait
        const siteInternet = await getForfaitField(
          props.updateData[0].id,
          "site_internet"
        );
        if (siteInternet) {
          setValue("site_internet", siteInternet);
        }
      } catch (error) {
        console.error(error);
      }
    };
    fetchData();
  }, [getClientField, getForfaitField, props.updateData, setValue]);

  async function onSubmit(values: InterventionType) {
    try {
      const dateIntervention =
        typeof values.date_intervention === "string"
          ? Timestamp.fromDate(new Date(values.date_intervention))
          : values.date_intervention;

      const envoiEmail = Boolean(values.envoi_email);
      const plugins = Boolean(values.plugins);
      const sauvegarde = Boolean(values.sauvegarde);
      const { idsites, site_internet, cms, ...others } = values;
      const newValues = {
        ...others,
        ...champsSitesIntervention({ idsites, site_internet, cms }, choix.sites, choix.parDefaut, false),
        date_intervention: dateIntervention,
        envoi_email: envoiEmail,
        plugins: plugins,
        sauvegarde: sauvegarde,
      };
      if (props.updateData[0].id) {
        await addDoc(collection(db, "intervention"), newValues);
      }
      props.onCloseModal();
      const newData: ForfaitType[] = await fetch();
      props.newData(newData);
    } catch (error) {
      console.error("Error adding data to Firebase:", error);
    }
  }

  const renderInput = (
    field: keyof InterventionType,
    placeholder: string,
    minLength: number,
    typeInput: string
  ) => {
    const maxNumber = 30;
    if (selectedIdForfait && typeInput === "client") {
      return (
        <>
          <Text>{clientName}</Text>
          <Box mb={2}>
            <UserIntervention
              id={selectedIdForfait.id}
              temps={selectedIdForfait.temps}
            />
          </Box>
        </>
      );
    } else if (typeInput === "number") {
      return (
        <FormControl isInvalid={!!errors[field]} mb={4} key={field}>
          <FormLabel htmlFor={field}>{placeholder}</FormLabel>
          <Controller
            name={field as "temps_passe"}
            control={control}
            defaultValue={0}
            rules={{
              required: "Champ obligatoire",
              min: {
                value: 0.1,
                message: "Le temps doit être supérieur à 0"
              }
            }}
            render={({ field: { onChange, value, name, ref } }) => (
              <NumberInput
                name={name}
                value={value}
                max={maxNumber}
                clampValueOnBlur={false}
                onChange={(_, valueNumber) => onChange(valueNumber)}
              >
                <NumberInputField ref={ref} />
                <NumberInputStepper>
                  <NumberIncrementStepper />
                  <NumberDecrementStepper />
                </NumberInputStepper>
              </NumberInput>
            )}
          />
          {/* Suggestions de temps rapides */}
          {field === "temps_passe" && (
            <ButtonGroup size="sm" mt={2} spacing={2}>
              <Button
                variant="outline"
                colorScheme="teal"
                onClick={() => setValue("temps_passe", 0.5, {
                  shouldValidate: true,
                  shouldDirty: true,
                  shouldTouch: true
                })}
              >
                0.5h
              </Button>
              <Button
                variant="outline"
                colorScheme="teal"
                onClick={() => setValue("temps_passe", 0.75, {
                  shouldValidate: true,
                  shouldDirty: true,
                  shouldTouch: true
                })}
              >
                0.75h
              </Button>
              <Button
                variant="outline"
                colorScheme="teal"
                onClick={() => setValue("temps_passe", 1, {
                  shouldValidate: true,
                  shouldDirty: true,
                  shouldTouch: true
                })}
              >
                1h
              </Button>
            </ButtonGroup>
          )}
          <FormErrorMessage>
            {errors[field] && errors[field].message}
          </FormErrorMessage>
        </FormControl>
      );
    } else if (typeInput === "boolean") {
      return (
        <FormControl isInvalid={!!errors[field]} mb={4} key={field}>
          <Checkbox name={field} {...register(field)}>
            {field}
          </Checkbox>
        </FormControl>
      );
    } else {
      // Utilisez le bloc else pour gérer d'autres types d'entrées
      // Date par défaut pour datetime-local
      const defaultValue = typeInput === "datetime-local"
        ? moment().format("YYYY-MM-DDTHH:mm")
        : "";

      return (
        <FormControl isInvalid={!!errors[field]} mb={4} key={field}>
          <FormLabel htmlFor={field}>{placeholder}</FormLabel>
          <Input
            id={field}
            placeholder={placeholder}
            {...register(field, {
              required: "Champ obligatoire",
              minLength: {
                value: minLength,
                message: `La longueur minimum est de ${minLength} caractères`,
              },
            })}
            defaultValue={defaultValue}
            type={typeInput}
          />
          {/* Suggestion CMS WordPress */}
          {field === "cms" && (
            <Tag
              mt={2}
              size="md"
              colorScheme="teal"
              cursor="pointer"
              onClick={() => setValue("cms", "WordPress", {
                shouldValidate: true,
                shouldDirty: true,
                shouldTouch: true
              })}
              _hover={{ opacity: 0.8 }}
            >
              WordPress
            </Tag>
          )}
          <FormErrorMessage>
            {errors[field] && errors[field].message}
          </FormErrorMessage>
        </FormControl>
      );
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      {renderInput(
        "date_intervention",
        "Date d'intervention",
        4,
        "datetime-local"
      )}
      {renderInput("idforfait", "Client", 4, "client")}
      {renderInput("temps_passe", "Temps", 2, "number")}
      <InterventionSites choix={choix} control={control} errors={errors} setValue={setValue} />
      {choix.pret && choix.sites.length === 0 && renderInput("site_internet", "Site internet", 4, "text")}
      {choix.pret && choix.sites.length === 0 && renderInput("cms", "CMS", 4, "text")}
      {renderInput("envoi_email", "Email", 1, "boolean")}
      {renderInput("plugins", "Plugins", 1, "boolean")}
      {renderInput("sauvegarde", "Sauvegarde", 1, "boolean")}

      <Button
        mt={4}
        colorScheme="teal"
        isLoading={isSubmitting}
        type="submit"
        mb={4}
      >
        Ajouter une intervention
      </Button>
    </form>
  );
}
